import React, { useEffect, useMemo, useState } from "react";

import LoadingSpinner from "../components/LoadingSpinner";
import { useAuth } from "../contexts/AuthContext";
import {
  ResponsiveDataTable,
  type ResponsiveColumn,
} from "../components/ResponsiveDataTable";
import { fmtDuration } from "../lib/format";
import { fetchUptime, MttrApiError } from "../services/mttrApi";
import type {
  CustomerUptime,
  ScuUptime,
  UptimeSnapshot,
} from "../types/uptime";

/**
 * Uptime page — fleet + per-customer + per-SCU availability over a
 * filterable window. Reads `uptime.json` (written by the runner each
 * cycle). Push H 2026-05-05.
 *
 * POWERED_DOWN never counts as down (per Jason 2026-05-05). The two
 * partial states are configurable via `[_jcox].dbo.app_config` keys
 * `UptimeCountScannerOnlyAsDown` / `UptimeCountCameraOnlyAsDown`. The
 * payload's `config` block surfaces the live setting so operators can
 * tell at a glance whether partial states count as down or up.
 */
const UptimePage: React.FC = () => {
  const [snapshot, setSnapshot] = useState<UptimeSnapshot | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(true);
  const [windowKey, setWindowKey] = useState<string>("7d");
  const [customerFilter, setCustomerFilter] = useState<string>("");
  // Customer users are already scoped to a single company — the
  // customer dropdown would only ever show one option besides
  // "All customers", so we hide it. Platform + vendor users (who
  // see multiple customers' boxes) keep the filter.
  const { role } = useAuth();
  const showCustomerFilter = role !== "customer";

  useEffect(() => {
    let active = true;
    void (async () => {
      try {
        const d = await fetchUptime();
        if (active) setSnapshot(d);
      } catch (e) {
        if (active) setError(e instanceof Error ? e : new MttrApiError(String(e)));
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const window = snapshot?.windows[windowKey];

  const customers = useMemo(() => {
    if (!window) return [""];
    const set = new Set<string>();
    for (const r of window.by_scu) {
      if (r.customer) set.add(r.customer);
    }
    return ["", ...Array.from(set).sort()];
  }, [window]);

  const filteredScus = useMemo(() => {
    if (!window) return [];
    const cust = customerFilter.toLowerCase();
    return window.by_scu.filter(
      (r) => !cust || (r.customer ?? "").toLowerCase() === cust,
    );
  }, [window, customerFilter]);

  if (loading && !snapshot) {
    return <LoadingSpinner label="loading uptime" />;
  }
  if (!snapshot) {
    return (
      <div className="p-6 text-sm text-error-700">
        Failed to load uptime.json. {error?.message ?? ""}
      </div>
    );
  }

  const windowKeys = Object.keys(snapshot.windows);

  return (
    <div className="p-4 md:p-6">
      <header className="mb-4 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div className="min-w-0 flex-1">
          <h1 className="text-xl font-bold">Uptime</h1>
          <div className="text-xs text-secondary-500 mt-0.5">
            Generated {new Date(snapshot.generated_utc).toLocaleString()} ·
            POWERED_DOWN does not count as downtime
          </div>
        </div>
        <div className="flex gap-2 overflow-x-auto min-w-0">
          {windowKeys.map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => setWindowKey(k)}
              className={
                "shrink-0 px-3 py-2 min-h-[44px] rounded-md text-sm border transition-colors " +
                (k === windowKey
                  ? "bg-primary-600 text-white border-primary-700"
                  : "bg-white border-secondary-300 hover:bg-secondary-50 active:bg-secondary-100")
              }
            >
              Last {k}
            </button>
          ))}
        </div>
      </header>

      {error && (
        <div className="mb-3 text-sm text-error-700 bg-error-50 px-3 py-2 rounded">
          {error.message}
        </div>
      )}

      {!window ? (
        <div className="text-sm text-secondary-500">
          No data for window <code>{windowKey}</code>.
        </div>
      ) : (
        <>
          <FleetCard
            uptime={window.fleet.uptime_percent}
            scuCount={window.fleet.scu_count}
            downSeconds={window.fleet.downtime_seconds_total}
            since={window.since}
            until={window.until}
            config={snapshot.config}
          />

          <section className="mt-6">
            <h2 className="text-base font-bold pb-1 border-b-2 border-primary-300">
              By customer ({window.by_customer.length})
            </h2>
            <div className="mt-3">
              <ResponsiveDataTable
                columns={CUSTOMER_COLUMNS}
                rows={window.by_customer}
                getRowKey={(c) => c.customer}
                mobileHeader={(c) => (
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-semibold">{c.customer}</span>
                    <UptimeBadge percent={c.uptime_percent} />
                  </div>
                )}
              />
            </div>
          </section>

          <section className="mt-6">
            <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between mb-2">
              <h2 className="text-base font-bold pb-1 border-b-2 border-primary-300 flex-1">
                By SCU ({filteredScus.length})
              </h2>
              {showCustomerFilter && (
                <select
                  value={customerFilter}
                  onChange={(e) => setCustomerFilter(e.target.value)}
                  className="md:ml-3 px-3 py-2 min-h-[44px] border border-secondary-300 rounded-md text-sm bg-white"
                >
                  {customers.map((c) => (
                    <option key={c || "(any)"} value={c}>
                      {c || "All customers"}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <ResponsiveDataTable
              columns={SCU_COLUMNS}
              rows={filteredScus}
              getRowKey={(r) => r.scu_name}
              mobileHeader={(r) => (
                <div className="flex items-center justify-between gap-2">
                  <code className="font-mono text-xs font-semibold">
                    {r.scu_name}
                  </code>
                  <UptimeBadge percent={r.uptime_percent} />
                </div>
              )}
            />
          </section>
        </>
      )}
    </div>
  );
};

interface FleetCardProps {
  uptime: number;
  scuCount: number;
  downSeconds: number;
  since: string;
  until: string;
  config: { count_scanner_only_as_down: boolean; count_camera_only_as_down: boolean };
}

const FleetCard: React.FC<FleetCardProps> = ({
  uptime,
  scuCount,
  downSeconds,
  since,
  until,
  config,
}) => (
  <section className="rounded-md border border-secondary-200 bg-white p-5">
    <div className="flex items-baseline justify-between">
      <h2 className="text-sm uppercase tracking-wide text-secondary-700 font-semibold">
        Fleet uptime
      </h2>
      <div className="text-xs text-secondary-500">
        {new Date(since).toLocaleDateString()} –{" "}
        {new Date(until).toLocaleDateString()} · {scuCount} SCUs
      </div>
    </div>
    <div className="mt-2 flex items-baseline gap-4">
      <div className="text-4xl font-bold">
        <UptimeBadge percent={uptime} large />
      </div>
      <div className="text-sm text-secondary-600">
        Total downtime (counted): <strong>{fmtDuration(downSeconds)}</strong>
      </div>
    </div>
    <div className="mt-2 text-xs text-secondary-500">
      SCANNER_ONLY counts as{" "}
      <strong>{config.count_scanner_only_as_down ? "down" : "up"}</strong> ·
      CAMERA_ONLY counts as{" "}
      <strong>{config.count_camera_only_as_down ? "down" : "up"}</strong> ·
      POWERED_DOWN <strong>up (always)</strong>
    </div>
  </section>
);

/**
 * Extract the smartbox identifier from the canonical SCU name. Examples:
 *   SCU-SB041XXX-GEN5 → SB041     (the XXX is a Rebound padding convention)
 *   SCU-PI7K9M4E-GEN5 → PI7K9M4E  (no XXX padding for Pi-named boxes)
 *   SCU-anything      → strip SCU- prefix and -GEN5 suffix
 */
function smartboxFromScu(scuName: string): string {
  return scuName
    .replace(/^SCU-/, "")
    .replace(/XXX-GEN5$/, "")
    .replace(/-GEN5$/, "");
}

const UptimeBadge: React.FC<{ percent: number; large?: boolean }> = ({
  percent,
  large,
}) => {
  const colour =
    percent >= 99.5
      ? "text-success-700"
      : percent >= 95
        ? "text-warning-700"
        : "text-error-700";
  return (
    <span className={`${colour} ${large ? "text-4xl" : "font-semibold"}`}>
      {percent.toFixed(2)}%
    </span>
  );
};

// Column definitions consumed by `ResponsiveDataTable`. Defined at
// module scope so they're stable across renders. Mobile-card headers
// duplicate the high-priority bits (customer/SCU + uptime), so those
// columns are `hideOnMobile` to avoid showing them twice.
const CUSTOMER_COLUMNS: ResponsiveColumn<CustomerUptime>[] = [
  {
    key: "customer",
    header: "Customer",
    hideOnMobile: true,
    cell: (c) => <span className="font-semibold">{c.customer}</span>,
  },
  {
    key: "scus",
    header: "SCUs",
    className: "text-right",
    cell: (c) => c.scu_count,
  },
  {
    key: "uptime",
    header: "Uptime %",
    className: "text-right",
    hideOnMobile: true,
    cell: (c) => <UptimeBadge percent={c.uptime_percent} />,
  },
  {
    key: "downtime",
    header: "Total downtime (counted)",
    mobileLabel: "Downtime",
    className: "text-right",
    cell: (c) => fmtDuration(c.downtime_seconds_total),
  },
];

const SCU_COLUMNS: ResponsiveColumn<ScuUptime>[] = [
  {
    key: "scu",
    header: "SCU",
    hideOnMobile: true,
    cell: (r) => <code className="font-mono text-xs">{r.scu_name}</code>,
  },
  {
    key: "customer",
    header: "Customer",
    cell: (r) => r.customer ?? "-",
  },
  {
    key: "smartbox",
    header: "Smartbox",
    cell: (r) => (
      <code className="font-mono text-xs">{smartboxFromScu(r.scu_name)}</code>
    ),
  },
  {
    key: "wc",
    header: "Work center",
    cell: (r) => r.work_center ?? "-",
  },
  {
    key: "uptime",
    header: "Uptime %",
    className: "text-right",
    hideOnMobile: true,
    cell: (r) => <UptimeBadge percent={r.uptime_percent} />,
  },
  {
    key: "down",
    header: "DOWN",
    className: "text-right",
    cell: (r) => fmtDuration(r.seconds_by_state.DOWN ?? 0),
  },
  {
    key: "pd",
    header: "PD",
    mobileLabel: "Powered down",
    className: "text-right text-secondary-500",
    cell: (r) => fmtDuration(r.seconds_by_state.POWERED_DOWN ?? 0),
  },
  {
    key: "so",
    header: "SO",
    mobileLabel: "Scanner only",
    className: "text-right",
    cell: (r) => fmtDuration(r.seconds_by_state.SCANNER_ONLY ?? 0),
  },
  {
    key: "co",
    header: "CO",
    mobileLabel: "Camera only",
    className: "text-right",
    cell: (r) => fmtDuration(r.seconds_by_state.CAMERA_ONLY ?? 0),
  },
];

export default UptimePage;
